import React, { useEffect, useState } from "react";
import {
  ImagePlus,
  FileText,
  PenLine,
  UploadCloud,
  X,
  Send,
  ShieldCheck,
} from "lucide-react";
import Swal from "sweetalert2";
import api from "../Api/Api";

export default function PostBlog() {
  const user_id = localStorage.getItem("user_id");

  const [isPosting, setIsPosting] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const [form, setForm] = useState({
    title: "",
    image: null,
    content: "",
    user_id: user_id || "",
  });

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "image") {
      const file = files?.[0];

      if (!file) return;

      if (!file.type.startsWith("image/")) {
        Swal.fire({
          icon: "error",
          title: "Invalid File",
          text: "Please select a valid image file.",
          confirmButtonColor: "#2563EB",
        });

        e.target.value = "";
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        Swal.fire({
          icon: "error",
          title: "File Too Large",
          text: "Featured image size must not exceed 2 MB.",
          confirmButtonColor: "#2563EB",
        });

        e.target.value = "";

        setForm((prev) => ({
          ...prev,
          image: null,
        }));

        setImagePreview(null);

        return;
      }

      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }

      setForm((prev) => ({
        ...prev,
        image: file,
      }));

      setImagePreview(URL.createObjectURL(file));

      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const removeImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }

    setImagePreview(null);

    setForm((prev) => ({
      ...prev,
      image: null,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.user_id) {
      Swal.fire({
        icon: "warning",
        title: "Sign In Required",
        text: "Please sign in before publishing an article.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    if (!form.title.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Title Required",
        text: "Please enter a title for your article.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    if (form.title.trim().length < 5) {
      Swal.fire({
        icon: "warning",
        title: "Title Too Short",
        text: "Please enter a more descriptive article title.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    if (form.content.trim().length < 50) {
      Swal.fire({
        icon: "warning",
        title: "Article Too Short",
        text: "Please add more content before publishing your article.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    const formData = new FormData();

    formData.append("title", form.title.trim());
    formData.append("content", form.content.trim());
    formData.append("user_id", form.user_id);

    if (form.image) {
      formData.append("image", form.image);
    }

    setIsPosting(true);

    try {
      const response = await api.post(
        "/postblog/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        await Swal.fire({
          icon: "success",
          title: "Article Published",
          text: "Your article has been published successfully.",
          confirmButtonColor: "#2563EB",
        });

        if (imagePreview) {
          URL.revokeObjectURL(imagePreview);
        }

        setForm({
          title: "",
          image: null,
          content: "",
          user_id: user_id || "",
        });

        setImagePreview(null);
      }
    } catch (error) {
      console.error("Blog post error:", error);

      Swal.fire({
        icon: "error",
        title: "Publish Failed",
        text:
          error.response?.data?.message ||
          error.response?.data?.detail ||
          "There was an error publishing your article. Please try again.",
        confirmButtonColor: "#2563EB",
      });
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Hero */}
      <section className="relative bg-slate-950 text-white pt-20 pb-28 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[450px] h-[450px] bg-blue-600/20 rounded-full blur-[140px]" />

        <div className="absolute -bottom-40 right-0 w-[450px] h-[450px] bg-cyan-400/10 rounded-full blur-[130px]" />

        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />

        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-400/20 bg-blue-500/10 text-blue-300 text-sm font-semibold mb-6">
            <PenLine size={15} />
            Nexindi Insights
          </div>

          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            Publish a new article
          </h1>

          <p className="text-slate-300 text-lg max-w-2xl leading-relaxed">
            Share technology insights, company updates, practical knowledge,
            and ideas with the Nexindi community.
          </p>
        </div>
      </section>

      {/* Editor */}
      <main className="relative -mt-14 pb-24 px-6">
        <div className="max-w-5xl mx-auto">
          <form
            onSubmit={handleSubmit}
            encType="multipart/form-data"
            className="bg-white border border-slate-200 rounded-3xl shadow-xl shadow-slate-900/5 overflow-hidden"
          >
            {/* Editor Header */}
            <div className="px-6 md:px-9 py-6 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="text-blue-600 text-sm font-semibold mb-1">
                  Article Editor
                </p>

                <h2 className="text-xl font-bold text-slate-900">
                  Create your content
                </h2>
              </div>

              <div className="inline-flex items-center gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5">
                <ShieldCheck size={15} className="text-blue-600" />
                Published under your account
              </div>
            </div>

            <div className="p-6 md:p-9 space-y-8">
              {/* Title */}
              <div>
                <div className="flex items-center justify-between gap-4 mb-2">
                  <label
                    htmlFor="title"
                    className="text-sm font-semibold text-slate-700"
                  >
                    Article Title
                  </label>

                  <span className="text-xs text-slate-400">
                    {form.title.length}/120
                  </span>
                </div>

                <div className="relative">
                  <FileText
                    size={19}
                    className="absolute left-4 top-4 text-slate-400"
                  />

                  <input
                    id="title"
                    type="text"
                    name="title"
                    value={form.title}
                    required
                    maxLength={120}
                    onChange={handleChange}
                    placeholder="Enter a compelling article title..."
                    className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-slate-200 bg-white outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />
                </div>
              </div>

              {/* Featured Image */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Featured Image
                </label>

                <p className="text-xs text-slate-400 mb-4">
                  Recommended landscape image. Maximum size: 2 MB.
                </p>

                {imagePreview ? (
                  <div className="relative rounded-2xl overflow-hidden border border-slate-200 group">
                    <img
                      src={imagePreview}
                      alt="Article preview"
                      className="w-full h-[280px] md:h-[380px] object-cover"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 via-transparent to-transparent" />

                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute top-4 right-4 w-10 h-10 rounded-xl bg-slate-950/70 backdrop-blur-md text-white flex items-center justify-center hover:bg-red-600 transition"
                      aria-label="Remove image"
                    >
                      <X size={18} />
                    </button>

                    <div className="absolute bottom-4 left-4">
                      <span className="inline-flex items-center gap-2 bg-white/90 backdrop-blur-md text-slate-700 rounded-xl px-3 py-2 text-xs font-semibold">
                        <ImagePlus size={14} />
                        Featured Image
                      </span>
                    </div>
                  </div>
                ) : (
                  <label className="group block border-2 border-dashed border-slate-300 rounded-2xl p-10 md:p-14 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-all duration-300">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-5 group-hover:bg-blue-600 group-hover:text-white transition">
                      <UploadCloud size={25} />
                    </div>

                    <h3 className="font-semibold text-slate-900 mb-2">
                      Upload featured image
                    </h3>

                    <p className="text-sm text-slate-500">
                      Click to browse for an image from your device.
                    </p>

                    <input
                      type="file"
                      name="image"
                      accept="image/*"
                      onChange={handleChange}
                      className="hidden"
                    />
                  </label>
                )}

                {imagePreview && (
                  <label className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-blue-600 cursor-pointer hover:text-blue-700">
                    <ImagePlus size={16} />
                    Replace image

                    <input
                      type="file"
                      name="image"
                      accept="image/*"
                      onChange={handleChange}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              {/* Content */}
              <div>
                <div className="flex items-center justify-between gap-4 mb-2">
                  <label
                    htmlFor="content"
                    className="text-sm font-semibold text-slate-700"
                  >
                    Article Content
                  </label>

                  <span className="text-xs text-slate-400">
                    {form.content.length.toLocaleString()} characters
                  </span>
                </div>

                <textarea
                  id="content"
                  name="content"
                  rows={16}
                  value={form.content}
                  required
                  onChange={handleChange}
                  placeholder="Start writing your article here..."
                  className="w-full px-5 py-4 rounded-2xl border border-slate-200 bg-white text-slate-700 leading-relaxed resize-y outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                />

                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-slate-400">
                    Minimum recommended length: 50 characters.
                  </p>

                  <p className="text-xs text-slate-400">
                    ~
                    {Math.max(
                      1,
                      Math.ceil(
                        form.content.trim().split(/\s+/).filter(Boolean).length /
                          200
                      )
                    )}{" "}
                    min read
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 md:px-9 py-6 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <p className="text-xs text-slate-500 max-w-md">
                Review your article carefully before publishing. Once
                published, it will be visible in Nexindi Insights.
              </p>

              <button
                type="submit"
                disabled={isPosting}
                className="group inline-flex items-center justify-center gap-3 bg-blue-600 text-white px-7 py-3.5 rounded-xl font-semibold hover:bg-blue-700 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-600/20 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {isPosting ? (
                  <>
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Publishing...
                  </>
                ) : (
                  <>
                    <Send
                      size={17}
                      className="transition-transform group-hover:translate-x-0.5"
                    />
                    Publish Article
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}